import React from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { getFormInitialValues } from "redux-form";
import { debounce } from "throttle-debounce";
import {
    checkTechnicalBulletinNumber,
    clearRelatedTechnicalBulletins,
} from "../../../../../../actions/ase/actionAseInternalTechnicalBulletinTicket";
import {
    FORM_FIELD_ISSUE_STATED,
    FORM_FIELD_PROBLEM_DESCRIPTION,
    FORM_FIELD_TECHNICAL_BULLETIN_NUMBER,
    FORM_LABEL_ISSUE_STATED,
    FORM_LABEL_PROBLEM_DESCRIPTION,
    FORM_LABEL_TECHNICAL_BULLETIN_NUMBER,
} from "../../../../../../constants/formConstants";
import { issueStated } from "../../../../../../constants/Utils";
import formMessages from "../../../../../../intl/afterSales/formMessages";
import issueStatedMessages from "../../../../../../intl/afterSales/issueStatedMessages";
import warnMessages from "../../../../../../intl/common/warnMessages";
import { ASE_INTERNAL_TECHNICAL_BULLETIN_TICKET } from "../../../../../../routes/paths";
import { formatInternalTicketNumber } from "../../../../../../utils/utils";
import { InputFieldWithValidation } from "../../../../../common/InputFieldWithValidation";
import InputSelectFieldWithValidation from "../../../../../common/InputSelectFieldWithValidation";
import BorderRadius from "../../../../../common/styled/BorderRadius";
import { TextareaFieldWithValidation } from "../../../../../common/TextareaFieldWithValidation";
import { validSixCharLength } from "../../../../../common/validationCommons";

const colClassName = "col-xl-3 col-lg-4 col-md-6 col-sm-12 pb-2";

const ProblemDescriptionSection = (props) => {
    const {
        reqFields,
        userRights,
        readOnly,
        checkTechnicalBulletinNumber,
        clearRelatedTechnicalBulletins,
        tickets,
        initialValues,
    } = props;

    const isEditTicket = !!(
        initialValues &&
        initialValues.ticketSection &&
        initialValues.ticketSection.uuid
    );

    const lastFetchedValue = React.useRef(null);

    const debouncedCheckTechnicalBulletinNumber = React.useMemo(
        () =>
            debounce(1000, (value) => {
                if (lastFetchedValue.current === value) return;
                lastFetchedValue.current = value;
                checkTechnicalBulletinNumber(value);
            }),
        [checkTechnicalBulletinNumber],
    );

    const handleTechnicalBulletinChange = React.useCallback(
        (value) => {
            if (value && value.length === 6) {
                debouncedCheckTechnicalBulletinNumber(value);
            } else if (tickets && tickets.length > 0) {
                lastFetchedValue.current = null;
                clearRelatedTechnicalBulletins();
            }
        },
        [
            debouncedCheckTechnicalBulletinNumber,
            clearRelatedTechnicalBulletins,
            tickets,
        ],
    );

    return (
        <>
            <h3 className="text-left ml-4">
                <FormattedMessage
                    {...formMessages.PROBLEM_DESCRIPTION_SECTION}
                />
            </h3>
            <BorderRadius
                size={5}
                className="border border-secondary px-4 py-2 bg-white"
            >
                <div className="form-row">
                    <div className={colClassName}>
                        <InputFieldWithValidation
                            label={
                                formMessages[
                                    FORM_LABEL_TECHNICAL_BULLETIN_NUMBER
                                ]
                            }
                            field={FORM_FIELD_TECHNICAL_BULLETIN_NUMBER}
                            isRequired={reqFields.includes(
                                FORM_FIELD_TECHNICAL_BULLETIN_NUMBER,
                            )}
                            isDisabled={
                                !userRights.canEditInternalTicketProblemDesc ||
                                readOnly
                            }
                            maxLength={6}
                            validations={[validSixCharLength]}
                            handleKeyUp={handleTechnicalBulletinChange}
                            isSmall
                        />
                        {!isEditTicket && tickets && tickets.length > 0 && (
                            <div style={{ color: "#ffcc33" }}>
                                <FormattedMessage
                                    {...warnMessages.ASE_TECHNICAL_BULLETIN_NUMBER_WARN001}
                                >
                                    {(text) => (
                                        <>
                                            {text}
                                            {tickets.map((ticket, index) => (
                                                <span key={ticket.id}>
                                                    {index > 0 && ", "}
                                                    <Link
                                                        to={`${ASE_INTERNAL_TECHNICAL_BULLETIN_TICKET}/view/${ticket.id}`}
                                                        target="_blank"
                                                    >
                                                        {formatInternalTicketNumber(
                                                            ticket.pfxNumber,
                                                            ticket.sfxNumber,
                                                        )}
                                                    </Link>
                                                </span>
                                            ))}
                                        </>
                                    )}
                                </FormattedMessage>
                            </div>
                        )}
                    </div>
                    <div className={colClassName}>
                        <InputSelectFieldWithValidation
                            label={formMessages[FORM_LABEL_ISSUE_STATED]}
                            field={FORM_FIELD_ISSUE_STATED}
                            isRequired={reqFields.includes(
                                FORM_FIELD_ISSUE_STATED,
                            )}
                            isDisabled={
                                !userRights.canEditInternalTicketProblemDesc ||
                                readOnly
                            }
                            options={issueStated}
                            intlMessages={issueStatedMessages}
                        />
                    </div>
                </div>
                <div className="form-row">
                    <div className="col-12 pr-0 pb-1">
                        <TextareaFieldWithValidation
                            label={formMessages[FORM_LABEL_PROBLEM_DESCRIPTION]}
                            field={FORM_FIELD_PROBLEM_DESCRIPTION}
                            isRequired={reqFields.includes(
                                FORM_FIELD_PROBLEM_DESCRIPTION,
                            )}
                            isDisabled={
                                !userRights.canEditInternalTicketProblemDesc ||
                                readOnly
                            }
                            rows={6}
                            maxLength={2000}
                        />
                    </div>
                </div>
            </BorderRadius>
        </>
    );
};

const mapStateToProps = (state, props) => ({
    initialValues: getFormInitialValues(props.form)(state),
    tickets: state.aseInternalTicket.relatedTechnicalBulletins,
});

export default injectIntl(
    connect(mapStateToProps, {
        checkTechnicalBulletinNumber,
        clearRelatedTechnicalBulletins,
    })(ProblemDescriptionSection),
);
